/* eslint-disable eqeqeq */
import { useOrganizations } from '@/features/organizations';
import Table from '@oncolens/oncolens-ui/lib/components/Common/Table';
import { Tooltip } from '@oncolens/oncolens-ui/lib/components/FormControls/Tooltip';
import React, { useMemo } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import { Group, getQipGroups } from '../api/qip';

interface Props {
  searchQuery: string;
}

const QipMetricGroupsTable = ({ searchQuery }: Props) => {
  const columns = React.useMemo(
    () => [
      { accessor: 'id', Header: 'Id' },
      { accessor: 'version', Header: 'Version' },
      {
        accessor: 'name',
        Header: 'Name',
      },
      { accessor: 'status', Header: 'Status' },
      {
        accessor: 'organizationIds',
        Header: 'Organizations',
        Cell: OrganizationsColumn,
        disableSortBy: true,
      },
      { accessor: 'updatedDate', Header: 'Last Edited', Cell: LastEditedDateColumn },
    ],
    [],
  );

  const filters = {
    ...(searchQuery && { Filters: `(Name)@=*${searchQuery}` }),
  };

  const initialSort = useMemo(() => [{ id: 'updatedDate', desc: true }], []);

  const navigate = useNavigate();
  const navigateToEdit = (rowData: any) => navigate(`${rowData.id}`);

  return (
    <Table
      columns={columns}
      onRowClick={navigateToEdit}
      fetchData={getQipGroups}
      queryBaseKey={'qip'}
      additionalQueryParameters={filters}
      options={{ textSize: 'sm' }}
      initialSort={initialSort}
    />
  );
};

interface QipMetricGroupsTableRowProps {
  row: {
    original: Group;
  };
}

const OrganizationsColumn = ({ row: { original: rowData } }: QipMetricGroupsTableRowProps) => {
  const { data: organizationData } = useOrganizations({
    pageSize: 500,
    options: {
      refetchOnMount: false,
      staleTime: Infinity,
    },
  });

  const organizationIds = rowData.organizationIds ?? [];

  const orgCount = (organizationIds || 0) && organizationIds.length;
  const mappedOrgs = organizationIds
    .map((id) => organizationData?.data.find((org) => org.id === id)?.name)
    .sort();

  return (
    <>
      {organizationIds && organizationIds.length < 1 && <p>-</p>}
      {organizationIds && organizationIds.length == 1 && (
        <p>{organizationData?.data?.find((x) => x.id === organizationIds[0])?.name ?? ''}</p>
      )}
      {orgCount > 1 && (
        <Tooltip
          text={mappedOrgs.map((name) => (
            <span className="block" key={name}>
              {name}
            </span>
          ))}
          icon={
            <p>
              <span className="flex items-center justify-center">
                {orgCount} Organizations <FaInfoCircle className="ml-1.5 text-blue-400" />
              </span>
            </p>
          }
        />
      )}
    </>
  );
};

const LastEditedDateColumn = ({ row: { original: rowData } }: QipMetricGroupsTableRowProps) => {
  const date = rowData.updatedDate.toLocaleString();
  return <span>{date}</span>;
};

export { QipMetricGroupsTable as default };
